import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Divider, Paper, Skeleton, Stack, Tooltip, Typography, useTheme, } from '@weave-mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { api } from '../api/apiClient';
import { queryKeys } from '../query/queryClient';
function formatEta(remainingSnaps, throughput) {
    if (!throughput || throughput <= 0)
        return '—';
    const hours = remainingSnaps / throughput;
    if (hours < 24)
        return `~${Math.round(hours)}h`;
    return `~${Math.round(hours / 24)}d`;
}
function chunk(arr, maxSize) {
    const chunks = [];
    for (let i = 0; i < arr.length; i += maxSize) {
        chunks.push(arr.slice(i, i + maxSize));
    }
    return chunks;
}
const CHUNK_SIZE = 100;
const REFETCH_INTERVAL_MS = 30000;
const BatchesOverviewPanel = ({ batchIds }) => {
    const theme = useTheme();
    const { data: allStatsData, isLoading } = useQuery({
        queryKey: [...queryKeys.batches.allStats(), batchIds.join(',')],
        queryFn: async () => {
            const chunks = chunk(batchIds, CHUNK_SIZE);
            const results = [];
            const concurrencyLimit = 5;
            for (let i = 0; i < chunks.length; i += concurrencyLimit) {
                const batch = chunks.slice(i, i + concurrencyLimit);
                const batchResults = await Promise.all(batch.map((ids) => api.batches.statsBulk(ids)));
                results.push(...batchResults);
            }
            return results.flatMap((r) => Object.values(r ?? {}));
        },
        refetchInterval: REFETCH_INTERVAL_MS,
        staleTime: 20000,
        enabled: batchIds.length > 0,
    });
    const now = new Date();
    const startDate = new Date(now.getTime() - 3 * 60 * 60 * 1000).toISOString().slice(0, 10);
    const endDate = now.toISOString().slice(0, 10);
    const { data: metricsData } = useQuery({
        queryKey: queryKeys.metrics.range(startDate, endDate, 'hourly'),
        queryFn: () => api.metrics.get(startDate, endDate, 'hourly'),
        refetchInterval: REFETCH_INTERVAL_MS,
        staleTime: 20000,
    });
    const aggregated = useMemo(() => {
        const allStats = allStatsData ?? [];
        let migHubsIP = 0, migHubsStuck = 0, migHubsPending = 0, migSnapsIP = 0, migSnapsStuck = 0, migSnapsPendingSum = 0;
        let valHubsIP = 0, valHubsStuck = 0, valHubsPending = 0, valSnapsIP = 0, valSnapsStuck = 0, valSnapsPendingSum = 0;
        let totalSnapshotsSum = 0;
        for (const s of allStats) {
            const m = s.migrationSnapshots;
            const v = s.validationSnapshots;
            const totalSnaps = s.totalSnapshots ?? 0;
            totalSnapshotsSum += totalSnaps;
            if (m) {
                const splitComputed = m.inProgress + m.stuck > 0;
                migHubsIP += splitComputed ? m.inProgress : (s.migration?.started ?? 0);
                migHubsStuck += splitComputed ? m.stuck : 0;
                migHubsPending += s.migration?.noStatus ?? 0;
                migSnapsIP += m.inProgressSnapshots;
                migSnapsStuck += m.stuckSnapshots;
                migSnapsPendingSum += Math.max(0, totalSnaps - m.inProgressSnapshots - m.stuckSnapshots);
            }
            if (v) {
                const splitComputed = v.inProgress + v.stuck > 0;
                valHubsIP += splitComputed ? v.inProgress : (s.validation?.started ?? 0);
                valHubsStuck += splitComputed ? v.stuck : 0;
                valHubsPending += s.validation?.noStatus ?? 0;
                valSnapsIP += v.inProgressSnapshots;
                valSnapsStuck += v.stuckSnapshots;
                valSnapsPendingSum += Math.max(0, totalSnaps - v.inProgressSnapshots - v.stuckSnapshots);
            }
        }
        const snapsKnown = totalSnapshotsSum > 0;
        const migSnapsPending = snapsKnown ? migSnapsPendingSum : null;
        const valSnapsPending = snapsKnown ? valSnapsPendingSum : null;
        const hourly = metricsData?.hourly ?? [];
        const last3 = hourly.slice(-3);
        const totalSnaps = last3.reduce((sum, h) => sum + (h.snapshotsMigrated ?? 0), 0);
        const throughputPerHour = last3.length > 0 ? totalSnaps / last3.length : null;
        return {
            migration: {
                hubsInProgress: migHubsIP,
                hubsStuck: migHubsStuck,
                hubsPending: migHubsPending,
                snapsInProgress: migSnapsIP,
                snapsStuck: migSnapsStuck,
                snapsPending: migSnapsPending,
            },
            validation: {
                hubsInProgress: valHubsIP,
                hubsStuck: valHubsStuck,
                hubsPending: valHubsPending,
                snapsInProgress: valSnapsIP,
                snapsStuck: valSnapsStuck,
                snapsPending: valSnapsPending,
            },
            throughputPerHour: throughputPerHour && throughputPerHour > 0 ? throughputPerHour : null,
        };
    }, [allStatsData, metricsData]);
    if (batchIds.length === 0)
        return null;
    if (isLoading) {
        return (_jsx(Box, { sx: { display: 'flex', gap: 2, mb: 3 }, children: [0, 1].map((i) => (_jsxs(Box, { sx: (t) => ({
                    flex: 1,
                    p: 3,
                    borderRadius: '12px',
                    border: `1px solid ${t.palette.divider}`,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    bgcolor: 'background.paper',
                }), children: [_jsx(Skeleton, { role: "progressbar", variant: "text", width: "40%", sx: { mb: 1 } }), _jsxs(Stack, { direction: "column", spacing: 2, children: [_jsx(Skeleton, { role: "progressbar", variant: "rectangular", width: "100%", height: 20 }), _jsx(Skeleton, { role: "progressbar", variant: "rectangular", width: "100%", height: 20 }), _jsx(Skeleton, { role: "progressbar", variant: "rectangular", width: "100%", height: 20 })] })] }, i))) }));
    }
    const migRemaining = (aggregated.migration.snapsPending ?? 0) + aggregated.migration.snapsInProgress;
    const valRemaining = (aggregated.validation.snapsPending ?? 0) + aggregated.validation.snapsInProgress;
    const StatRow = ({ label, value, valueColor, tooltip }) => (_jsxs(Box, { sx: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5 }, children: [_jsx(Typography, { variant: "body2", color: "text.secondary", children: label }), _jsx(Tooltip, { title: tooltip, placement: "top", children: _jsx(InfoOutlinedIcon, { sx: { fontSize: 12, color: 'text.disabled', cursor: 'help' } }) })] }), _jsx(Typography, { variant: "body1", sx: { fontWeight: 'bold', color: valueColor ?? 'text.primary' }, children: String(value) })] }));
    const Column = ({ heading, children, }) => (_jsxs(Box, { sx: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }, children: [_jsx(Typography, { variant: "overline", color: "text.secondary", sx: { lineHeight: 1, display: 'block', fontWeight: 600 }, children: heading }), children] }));
    const renderCard = (title, kind, section, remaining, testIdPrefix) => {
        const isStuck = section.hubsStuck > 0;
        const throughputStr = aggregated.throughputPerHour
            ? `~${Math.round(aggregated.throughputPerHour)}/hr`
            : '—';
        const etaStr = formatEta(remaining, aggregated.throughputPerHour);
        const verb = kind === 'migration' ? 'migrated' : 'validated';
        const action = kind === 'migration' ? 'migration' : 'validation';
        return (_jsxs(Paper, { "data-testid": isStuck ? `stuck-warning-${testIdPrefix}` : undefined, elevation: 0, sx: (t) => ({
                flex: 1,
                p: 3,
                borderRadius: '12px',
                border: `1px solid ${t.palette.divider}`,
                borderLeft: isStuck ? `3px solid ${t.palette.warning.main}` : '3px solid transparent',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                bgcolor: 'background.paper',
            }), children: [_jsx(Typography, { variant: "subtitle1", sx: {
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        fontSize: '12px',
                        letterSpacing: '0.7px',
                        color: 'text.primary',
                        mb: 1.5,
                    }, children: title }), _jsx(Divider, { sx: { mb: 2 } }), _jsxs(Box, { sx: { display: 'flex', gap: 3 }, children: [_jsxs(Column, { heading: "Hubs", children: [_jsx(StatRow, { label: "In Progress", value: section.hubsInProgress, valueColor: theme.palette.info.main, tooltip: `Hubs currently being ${verb} across all batches` }), _jsx(StatRow, { label: "Stuck", value: section.hubsStuck, valueColor: isStuck ? theme.palette.warning.main : theme.palette.text.secondary, tooltip: `Hubs whose ${action} has stalled with no recent progress` }), _jsx(StatRow, { label: "Pending", value: section.hubsPending, valueColor: theme.palette.text.secondary, tooltip: `Hubs with no ${action} status yet` })] }), _jsx(Divider, { orientation: "vertical", flexItem: true }), _jsxs(Column, { heading: "Snapshots", children: [_jsx(StatRow, { label: "In Progress", value: section.snapsInProgress, valueColor: theme.palette.info.main, tooltip: `Snapshots actively being ${verb} right now` }), _jsx(StatRow, { label: "Stuck", value: section.snapsStuck, valueColor: section.snapsStuck > 0 ? theme.palette.warning.main : theme.palette.text.secondary, tooltip: `Snapshots whose ${action} has stalled during processing` }), _jsx(StatRow, { label: "Pending", value: section.snapsPending ?? '—', valueColor: theme.palette.text.secondary, tooltip: section.snapsPending === null
                                        ? 'Snapshot count unavailable — re-upload batch with a snapshot_count column to enable this stat'
                                        : `Snapshots not yet in-progress or stuck (totalSnapshots minus in-progress and stuck)` })] }), _jsx(Divider, { orientation: "vertical", flexItem: true }), _jsxs(Column, { heading: "Throughput & ETA", children: [_jsx(StatRow, { label: "Throughput", value: throughputStr, tooltip: `Average snapshots ${verb} per hour over the last 3 hours` }), _jsx(StatRow, { label: "ETA", value: etaStr, valueColor: theme.palette.text.secondary, tooltip: `Estimated time to complete in-progress and pending ${action} at current throughput` })] })] })] }, testIdPrefix));
    };
    return (_jsxs(Box, { sx: { display: 'flex', gap: 2, mb: 3 }, children: [renderCard('Migration', 'migration', aggregated.migration, migRemaining, 'migration'), renderCard('Validation', 'validation', aggregated.validation, valRemaining, 'validation')] }));
};
export default BatchesOverviewPanel;
